import { useMemo, useState } from 'react';

/**
 * Lightweight sorting hook for simple tables
 *
 * Use this when you need:
 * - Basic sorting functionality with custom sort functions
 * - Custom table markup/structure (not using TanStack Table)
 * - Minimal dependencies
 * - Full control over rendering
 * - Three-state sorting: unsorted → asc → desc → unsorted
 *
 * For complex tables with pagination, filtering, etc., use ClientDataTable instead.
 *
 * @example
 * const sortFunctions = {
 *   name: (a, b) => a.name.localeCompare(b.name),
 *   age: (a, b) => a.age - b.age,
 * };
 *
 * const { sortedData, sortColumn, sortDirection, handleSort } = useSimpleTableSort({
 *   data: users,
 *   sortFunctions,
 * });
 */

export type SortDirection = 'asc' | 'desc';

interface UseSimpleTableSortOptions<T, K extends string> {
    data: T[];
    sortFunctions: Record<K, (a: T, b: T) => number>;
    initialSort?: {
        column: K;
        direction: SortDirection;
    };
}

interface UseSimpleTableSortReturn<T, K extends string> {
    sortedData: T[];
    sortColumn: K | null;
    sortDirection: SortDirection;
    handleSort: (column: K) => void;
}

export function useSimpleTableSort<T, K extends string>({
    data,
    sortFunctions,
    initialSort,
}: UseSimpleTableSortOptions<T, K>): UseSimpleTableSortReturn<T, K> {
    const [sortColumn, setSortColumn] = useState<K | null>(initialSort?.column ?? null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort?.direction ?? 'asc');

    const handleSort = (column: K) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else {
                // Reset to unsorted state
                setSortColumn(null);
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedData = useMemo(() => {
        if (!sortColumn) return data;

        const dataCopy = [...data];
        const sortFunction = sortFunctions[sortColumn];

        if (sortFunction) {
            dataCopy.sort((a, b) => {
                const comparison = sortFunction(a, b);
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return dataCopy;
    }, [data, sortColumn, sortDirection, sortFunctions]);

    return {
        sortedData,
        sortColumn,
        sortDirection,
        handleSort,
    };
}
